import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { createChallenge, getChallenge, updateChallenge } from '../../lib/challenges';

// Searchable Multi-Select Component
function SearchableMultiSelect({ 
  label, 
  options, 
  selected, 
  onChange, 
  placeholder = "Search and select..." 
}: { 
  label: string; 
  options: string[]; 
  selected: string[]; 
  onChange: (selected: string[]) => void;
  placeholder?: string;
}) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase()) && !selected.includes(opt)
  );

  function toggleOption(opt: string) {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  }

  function removeOption(opt: string) {
    onChange(selected.filter(s => s !== opt));
  }

  return (
    <div className="block relative">
      <div className="font-medium mb-1">{label}</div>
      <div className="relative">
        <div 
          className="border rounded px-3 py-2 w-full cursor-pointer bg-white min-h-[42px] flex flex-wrap gap-1 items-center"
          onClick={() => setIsOpen(!isOpen)}
        >
          {selected.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            selected.map(opt => (
              <span key={opt} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm flex items-center gap-1">
                {opt}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(opt);
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-auto">
            <input
              type="text"
              className="w-full px-3 py-2 border-b"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            <div className="max-h-48 overflow-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-gray-500 text-sm">No options found</div>
              ) : (
                filtered.map(opt => (
                  <div
                    key={opt}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => toggleOption(opt)}
                  >
                    {opt}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {isOpen && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

const TRANSLATION_LANGUAGE_OPTIONS = [
  { value: 'vi', label: 'Vietnamese' },
  { value: 'es', label: 'Spanish' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'tl', label: 'Filipino' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'pt', label: 'Português (Brasil)' },
  { value: 'th', label: 'ภาษาไทย' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
];

export default function ChallengeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  // Date helpers
  const formatDateTimeLocal = (ms: number) => {
    const d = new Date(ms);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeLocal = (value: string) => {
    return new Date(value).getTime();
  };

  const [form, setForm] = useState<any>({
    title: '',
    description: '',
    status: 'scheduled',
    startAt: Date.now() + 3600_000,
    endAt: Date.now() + 7*24*3600_000,
    requiredCount: 10,
    pixReward: 1000,
    requireUniqueTasks: false,
    targetLanguage: 'all',
    eligible: { categories: [], subcategories: [], taskIds: [] },
    imageUrl: '',
    i18n: { title: {}, description: {} },
  });
  const [activeTab, setActiveTab] = useState<'details' | 'translations'>('details');
  const [newTranslationLang, setNewTranslationLang] = useState('');
  const [newTranslationTitle, setNewTranslationTitle] = useState('');
  const [newTranslationDescription, setNewTranslationDescription] = useState('');
  const [editingTranslationLang, setEditingTranslationLang] = useState<string | null>(null);
  const [editingTranslationTitle, setEditingTranslationTitle] = useState('');
  const [editingTranslationDescription, setEditingTranslationDescription] = useState('');
  const [file, setFile] = useState<File|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);

  // Fetch tasks for dropdowns
  const [tasks, setTasks] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [subcategories, setSubcategories] = useState<string[]>([]);

  useEffect(() => {
    const tasksQuery = query(collection(db, 'tasks'), orderBy('title', 'asc'));
    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
      
      // Extract unique categories and subcategories
      const cats = Array.from(new Set(tasksData.map((t: any) => t.category).filter(Boolean))) as string[];
      const subcats = Array.from(new Set(tasksData.map((t: any) => t.subcategory).filter(Boolean))) as string[];
      setCategories(cats.sort());
      setSubcategories(subcats.sort());
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isNew && id) {
      getChallenge(id).then((c: any) => {
        if (!c) return;
        setForm((f: any) => ({ ...f, ...c, i18n: c.i18n || { title: {}, description: {} } }));
        setAnalytics(c.analytics || null);
      });
    }
  }, [id, isNew]);

  async function onSave() {
    setLoading(true);
    try {
      if (isNew) {
        const newId = await createChallenge({
          title: form.title,
          description: form.description,
          imageUrl: form.imageUrl,
          startAt: Number(form.startAt),
          endAt: Number(form.endAt),
          status: form.status,
          requiredCount: Number(form.requiredCount),
          pixReward: Number(form.pixReward),
          requireUniqueTasks: form.requireUniqueTasks || false,
          targetLanguage: form.targetLanguage,
          eligible: form.eligible,
          i18n: form.i18n || { title: {}, description: {} },
        }, file);
        navigate(`/challenges/${newId}`);
      } else if (id) {
        await updateChallenge(id, {
          title: form.title,
          imageUrl: form.imageUrl,
          description: form.description,
          startAt: Number(form.startAt),
          endAt: Number(form.endAt),
          status: form.status,
          requiredCount: Number(form.requiredCount),
          pixReward: Number(form.pixReward),
          requireUniqueTasks: form.requireUniqueTasks || false,
          targetLanguage: form.targetLanguage,
          eligible: form.eligible,
          i18n: form.i18n || { title: {}, description: {} },
        }, file);
      }
    } finally {
      setLoading(false);
    }
  }

  function updateField(path: string, value: any) {
    setForm((f: any) => {
      const copy = { ...f };
      const parts = path.split('.');
      let cur: any = copy;
      for (let i=0;i<parts.length-1;i++) cur = cur[parts[i]];
      cur[parts[parts.length-1]] = value;
      return copy;
    });
  }

  function addTranslation() {
    if (!newTranslationLang || !newTranslationTitle || !newTranslationDescription) return;
    setForm((f: any) => ({
      ...f,
      i18n: {
        title: { ...(f.i18n?.title || {}), [newTranslationLang]: newTranslationTitle },
        description: { ...(f.i18n?.description || {}), [newTranslationLang]: newTranslationDescription },
      }
    }));
    setNewTranslationLang('');
    setNewTranslationTitle('');
    setNewTranslationDescription('');
  }

  function updateTranslation() {
    if (!editingTranslationLang) return;
    setForm((f: any) => ({
      ...f,
      i18n: {
        title: { ...(f.i18n?.title || {}), [editingTranslationLang]: editingTranslationTitle },
        description: { ...(f.i18n?.description || {}), [editingTranslationLang]: editingTranslationDescription },
      }
    }));
    setEditingTranslationLang(null);
  }

  function deleteTranslation(lang: string) {
    setForm((f: any) => {
      const newTitle = { ...(f.i18n?.title || {}) };
      const newDesc = { ...(f.i18n?.description || {}) };
      delete newTitle[lang];
      delete newDesc[lang];
      return { ...f, i18n: { title: newTitle, description: newDesc } };
    });
  }

  const taskOptions = tasks.map(t => `${t.id} - ${t.title || 'Untitled'}`);

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => navigate('/challenges')}
          className="px-3 py-2 border rounded hover:bg-gray-50 flex items-center gap-2"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold">{isNew ? 'New Challenge' : 'Edit Challenge'}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border rounded p-4 space-y-4">
          <div className="flex border-b mb-4">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'details' ? 'border-b-2 border-black' : 'text-gray-500'}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'translations' ? 'border-b-2 border-black' : 'text-gray-500'}`}
              onClick={() => setActiveTab('translations')}
            >
              Translations
            </button>
          </div>
          {activeTab === 'details' && (
            <>
          <label className="block">
            <div className="font-medium">Title</div>
            <input className="mt-1 border rounded px-3 py-2 w-full"
              value={form.title} onChange={e=>updateField('title', e.target.value)} />
          </label>

          <label className="block">
            <div className="font-medium">Description</div>
            <textarea className="mt-1 border rounded px-3 py-2 w-full min-h-[80px]"
              value={form.description ?? ''} onChange={e=>updateField('description', e.target.value)} />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <div className="font-medium">Status</div>
              <select className="mt-1 border rounded px-3 py-2 w-full"
                value={form.status} onChange={e=>updateField('status', e.target.value)}>
                <option value="scheduled">scheduled</option>
                <option value="active">active</option>
                <option value="ended">ended</option>
              </select>
            </label>
            <label className="block">
              <div className="font-medium">PIX Reward</div>
              <input type="number" className="mt-1 border rounded px-3 py-2 w-full"
                value={form.pixReward} onChange={e=>updateField('pixReward', e.target.value)} />
            </label>
          </div>

          <label className="block">
            <div className="font-medium">Target Language</div>
            <select className="mt-1 border rounded px-3 py-2 w-full"
              value={form.targetLanguage ?? 'all'} onChange={e=>updateField('targetLanguage', e.target.value)}>
              <option value="all">All Languages (Default)</option>
              <option value="vi">Vietnamese</option>
              <option value="es">Spanish</option>
              <option value="id">Bahasa Indonesia</option>
              <option value="tl">Filipino</option>
              <option value="hi">हिन्दी</option>
              <option value="pt">Português (Brasil)</option>
              <option value="th">ภาษาไทย</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
              <option value="it">Italiano</option>
            </select>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <div className="font-medium">Start Date & Time</div>
              <input 
                type="datetime-local" 
                className="mt-1 border rounded px-3 py-2 w-full"
                value={formatDateTimeLocal(form.startAt)}
                onChange={e=>updateField('startAt', parseDateTimeLocal(e.target.value))} 
              />
            </label>
            <label className="block">
              <div className="font-medium">End Date & Time</div>
              <input 
                type="datetime-local" 
                className="mt-1 border rounded px-3 py-2 w-full"
                value={formatDateTimeLocal(form.endAt)}
                onChange={e=>updateField('endAt', parseDateTimeLocal(e.target.value))} 
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <div className="font-medium">Required Count</div>
              <input type="number" className="mt-1 border rounded px-3 py-2 w-full"
                value={form.requiredCount} onChange={e=>updateField('requiredCount', e.target.value)} />
            </label>
            <label className="block">
              <div className="font-medium">Require Unique Tasks</div>
              <div className="mt-1 flex items-center">
                <input 
                  type="checkbox" 
                  className="mr-2"
                  checked={form.requireUniqueTasks || false}
                  onChange={e=>updateField('requireUniqueTasks', e.target.checked)} 
                />
                <span className="text-sm text-gray-600">
                  Each task can only be counted once
                </span>
              </div>
              {form.requireUniqueTasks && (
                <div className="text-xs text-blue-600 mt-1">
                  ✓ Users must complete different tasks (e.g., all 52 letters)
                </div>
              )}
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-600 mb-2">
                <strong>Eligibility Rules:</strong> Leave fields empty to allow all. If you set Category and Subcategory, 
                it will match <strong>any task</strong> with those values. Only set Task IDs if you want to restrict to specific tasks.
              </div>
            </div>
            <SearchableMultiSelect
              label="Categories (optional - leave empty for all categories)"
              options={categories}
              selected={form.eligible?.categories || []}
              onChange={(selected) => updateField('eligible.categories', selected)}
              placeholder="Select categories or leave empty for all"
            />
            <SearchableMultiSelect
              label="Subcategories (optional - leave empty for all subcategories)"
              options={subcategories}
              selected={form.eligible?.subcategories || []}
              onChange={(selected) => updateField('eligible.subcategories', selected)}
              placeholder="Select subcategories or leave empty for all"
            />
            <SearchableMultiSelect
              label="Task IDs (optional - leave empty to match all tasks in selected categories/subcategories)"
              options={taskOptions}
              selected={(form.eligible?.taskIds || []).map((tid: string) => {
                const task = tasks.find(t => t.id === tid);
                return task ? `${tid} - ${task.title || 'Untitled'}` : tid;
              })}
              onChange={(selected) => {
                // Extract just the task ID (before the " - ")
                const taskIds = selected.map(s => s.split(' - ')[0]);
                updateField('eligible.taskIds', taskIds);
              }}
              placeholder="Select specific tasks or leave empty for all matching tasks"
            />
          </div>

          <label className="block">
            <div className="font-medium">Image</div>
            <input type="file" className="mt-1" onChange={e=>setFile(e.target.files?.[0])}/>
            {form.imageUrl && <img alt="" src={form.imageUrl} className="mt-2 h-24 rounded border" />}
          </label>

          <div className="flex gap-3">
            <button onClick={onSave} disabled={loading} className="px-4 py-2 bg-black text-white rounded">
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
            </>
          )}
          {activeTab === 'translations' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Add translated titles and descriptions for non-English languages. Users will only see this challenge/contest if a translation exists for their language.</p>

              {TRANSLATION_LANGUAGE_OPTIONS.filter(opt =>
                form.i18n?.title?.[opt.value] || form.i18n?.description?.[opt.value]
              ).map(opt => (
                <div key={opt.value} className="border rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{opt.label}</span>
                    <div className="flex gap-2">
                      <button type="button" className="text-sm text-blue-600 hover:underline"
                        onClick={() => {
                          setEditingTranslationLang(opt.value);
                          setEditingTranslationTitle(form.i18n?.title?.[opt.value] || '');
                          setEditingTranslationDescription(form.i18n?.description?.[opt.value] || '');
                        }}>Edit</button>
                      <button type="button" className="text-sm text-red-600 hover:underline"
                        onClick={() => deleteTranslation(opt.value)}>Delete</button>
                    </div>
                  </div>
                  {editingTranslationLang === opt.value ? (
                    <div className="space-y-2">
                      <input className="border rounded px-3 py-2 w-full" placeholder="Translated title"
                        value={editingTranslationTitle} onChange={e => setEditingTranslationTitle(e.target.value)} />
                      <textarea className="border rounded px-3 py-2 w-full min-h-[60px]" placeholder="Translated description"
                        value={editingTranslationDescription} onChange={e => setEditingTranslationDescription(e.target.value)} />
                      <div className="flex gap-2">
                        <button type="button" className="px-3 py-1 bg-black text-white rounded text-sm"
                          onClick={updateTranslation}>Save</button>
                        <button type="button" className="px-3 py-1 border rounded text-sm"
                          onClick={() => setEditingTranslationLang(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm"><span className="font-medium">Title:</span> {form.i18n?.title?.[opt.value]}</p>
                      <p className="text-sm"><span className="font-medium">Description:</span> {form.i18n?.description?.[opt.value]}</p>
                    </div>
                  )}
                </div>
              ))}

              <div className="border rounded p-3 space-y-2">
                <div className="font-medium text-sm">Add Translation</div>
                <select className="border rounded px-3 py-2 w-full"
                  value={newTranslationLang} onChange={e => setNewTranslationLang(e.target.value)}>
                  <option value="">Select language...</option>
                  {TRANSLATION_LANGUAGE_OPTIONS.filter(opt =>
                    !form.i18n?.title?.[opt.value]
                  ).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input className="border rounded px-3 py-2 w-full" placeholder="Translated title"
                  value={newTranslationTitle} onChange={e => setNewTranslationTitle(e.target.value)} />
                <textarea className="border rounded px-3 py-2 w-full min-h-[60px]" placeholder="Translated description"
                  value={newTranslationDescription} onChange={e => setNewTranslationDescription(e.target.value)} />
                <button type="button" className="px-3 py-1 bg-black text-white rounded text-sm"
                  onClick={addTranslation}
                  disabled={!newTranslationLang || !newTranslationTitle || !newTranslationDescription}>
                  Add Translation
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="border rounded p-4 space-y-2">
          <div className="font-semibold">Analytics</div>
          {!analytics && <div className="text-sm text-gray-500">No data yet</div>}
          {analytics && (
            <ul className="text-sm">
              <li>Participants: {analytics.participantsCount || 0}</li>
              <li>Completions: {analytics.completionsCount || 0}</li>
              <li>PIX Paid: {analytics.pixPaidTotal || 0}</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
